// Spring letters: a word split into one span per letter, each a spring coupled to its neighbours, so a jolt runs
// along the word as a wave. The word itself leans (skewX) towards a target with its own spring.
// Used by any `[data-spring]` heading (components.ts).

export type Letter = { el: HTMLElement; y: number; vy: number; r: number; vr: number; rest: number };
export type Word = { el: HTMLElement; letters: Letter[]; sk: number; vsk: number; skT: number; k: number };

// Real text stays for screen readers; the visible copy becomes one span per letter.
export function split(el: HTMLElement): Word {
  const text = el.textContent ?? '';
  el.textContent = '';
  const sr = document.createElement('span');
  sr.className = 'sr-only';
  sr.textContent = text;
  el.append(sr);
  const letters: Letter[] = [];
  text.split(' ').forEach((word, i) => {
    if (i) el.append(' '); // a real space: lines wrap only here, never inside a word
    const w = document.createElement('span');
    w.className = 'wrd';
    w.setAttribute('aria-hidden', 'true');
    for (const ch of word) {
      const l = document.createElement('span');
      l.className = 'ltr';
      l.textContent = ch;
      w.append(l);
      letters.push({ el: l, y: 0, vy: 0, r: 0, vr: 0, rest: 0 });
    }
    el.append(w);
  });
  letters.forEach((l) => l.el.addEventListener('pointerenter', () => { l.vy -= 700; l.vr += (Math.random() - 0.5) * 1400; }));
  return { el, letters, sk: 0, vsk: 0, skT: 0, k: 180 };
}

export function stepWord(w: Word, dt: number): void {
  w.vsk += (w.k * (w.skT - w.sk) - 9 * w.vsk) * dt;
  w.sk += w.vsk * dt;
  const L = w.letters;
  for (let i = 0; i < L.length; i++) {
    const l = L[i];
    const nb = (L[i - 1]?.y ?? l.y) + (L[i + 1]?.y ?? l.y) - 2 * l.y;
    l.vy += (260 * (l.rest - l.y) + 900 * nb - 14 * l.vy) * dt;
    l.vr += (-260 * l.r - 14 * l.vr) * dt;
  }
  for (const l of L) { l.y += l.vy * dt; l.r += l.vr * dt; }
}

export function renderWord(w: Word): void {
  w.el.style.transform = `skewX(${w.sk.toFixed(2)}deg)`;
  for (const l of w.letters) l.el.style.transform = `translate3d(0,${l.y.toFixed(2)}px,0) rotate(${l.r.toFixed(2)}deg)`;
}

// True once every letter is (almost) still: callers can skip rendering.
export function atRest(w: Word): boolean {
  return Math.abs(w.sk) + Math.abs(w.vsk) < 0.01
    && w.letters.every((l) => Math.abs(l.y - l.rest) + Math.abs(l.vy) + Math.abs(l.r) + Math.abs(l.vr) < 0.02);
}

// Jolt the letters nearest one end (a step, a shove), fading along the word.
export function jolt(w: Word, fromEnd: boolean, amp: number): void {
  const n = w.letters.length;
  w.letters.forEach((l, i) => { l.vy -= amp * Math.exp(-(fromEnd ? n - 1 - i : i) / 2); });
}
